// AboutUs.jsx
// ─────────────────────────────────────────────────────────────────────────────
// "About Us" page for KinetiQ.  Route: /about-us   (see App.js)
//
// WHAT THE PAGE CONTAINS
//   0. Navbar          – the landing page's navbar (About Us tab highlighted)
//   0. Banner          – full-width page-title image: chemical plant at night + lab glassware
//   1. Hero            – what KinetiQ is, in one sentence, over a kinetics curve
//   2. Why we built it – the problem, and what KinetiQ takes off the user's plate
//   3. What we stand for – four working principles (array: PRINCIPLES)
//   4. The team        – array: TEAM        ← replace the placeholder entries
//   5. Contact band    – links to /contact and /login
//
// EDITING THE CONTENT
//   All wording lives in the plain arrays / strings at the top of this file
//   (STORY_*, TAKEN_OFF_YOUR_PLATE, PRINCIPLES, TEAM). Change the text there;
//   no JSX changes are needed.
//
// SCROLLING
//   DataFittingPage.css sets `html, body { overflow: hidden }`, so (exactly like
//   KineticLandingPage) this page is its own scroll container — see
//   `.about-page` in AboutUs.css. The ref is passed to <Navbar> so the navbar
//   shadow follows that container's scroll position.
// ─────────────────────────────────────────────────────────────────────────────

import React, { useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
// Reuse the landing page's own navbar (same tabs, same look) and its register pop-up.
import { Nav, RegisterHelpModal } from './KineticLandingPage';
import '../styles/AboutUs.css';

// ── Content ──────────────────────────────────────────────────────────────────

const HERO_TITLE = 'Kinetic modelling should take an afternoon, not a software project.';
const HERO_TEXT =
  'KinetiQ is a browser-based platform that turns experimental reaction data ' +
  'into fitted kinetic parameters. Researchers and process engineers can spend ' +
  'their time on the chemistry instead of on code.';

const STORY_TITLE = 'Why we built KinetiQ';
const STORY_PARAGRAPHS = [
  'Fitting a rate law to experimental data usually means writing a reactor ' +
  'model, setting up an optimizer, plotting the result and moving the numbers ' +
  'into the next program. Each of those steps is a place where time is lost ' +
  'and mistakes creep in.',
  'KinetiQ does that work for you. You describe your reactions and upload your ' +
  'data; KinetiQ builds the reactor model, searches for the best parameters ' +
  'and shows you how closely the model follows your experiments.',
];

const TAKEN_OFF_YOUR_PLATE = [
  'Writing and debugging reactor-model code',
  'Setting up and tuning an optimizer',
  'Plotting the fit against your data by hand',
  'Retyping fitted parameters into design tools',
];

const PRINCIPLES = [
  {
    title: 'Rigorous by default',
    body: 'The fit runs against your experimental data with a Bayesian optimizer, and you can see for yourself how well the model matches.',
  },
  {
    title: 'Open to real experiments',
    body: 'Batch or continuous operation, your own temperatures, pressures, catalyst amounts and volumes, several data sets, several reactions.',
  },
  {
    title: 'Grounded in the literature',
    body: 'Worked examples are built from published experimental studies, so you can check KinetiQ against results you already trust.',
  },
  {
    title: 'Easy to pick up',
    body: 'A modern interface in your browser, with nothing to install. The fitted parameters are ready to use in other programs for reactor design.',
  },
];

// ── EDIT: replace these placeholders with the real team ───────────────────────
// `name` and `role` are shown on the page; the avatar uses the initials of `name`.
const TEAM = [
  { name: 'Team member name', role: 'Role', bio: 'One or two sentences about this person\u2019s background and what they do on KinetiQ.' },
  { name: 'Team member name', role: 'Role', bio: 'One or two sentences about this person\u2019s background and what they do on KinetiQ.' },
  { name: 'Team member name', role: 'Role', bio: 'One or two sentences about this person\u2019s background and what they do on KinetiQ.' },
];

const initialsOf = (name) =>
  name.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0].toUpperCase()).join('');

// ── Banner ───────────────────────────────────────────────────────────────────
// The banner is drawn with the original illustrations below (a chemical plant at
// night and a lab bench), so the page needs no image files.
// The banner uses the photo below. The file must be in /public (so it is served as
// /about-banner.jpg). Set BANNER_PHOTO = '' to go back to the drawn illustrations.
const BANNER_PHOTO = '/chemical_industry.png';
const BANNER_TITLE = 'About Us';
const BANNER_TEXT = 'Kinetic modelling for chemical and process engineering.';

// Small deterministic generator so the lit windows are the same on every render.
const seeded = (seed) => () => {
  seed = (seed * 16807) % 2147483647;
  return (seed - 1) / 2147483646;
};

// Columns, towers and tanks of the plant: [x, width, height, kind]
const PLANT_TOWERS = [
  [70, 46, 150, 'column'], [190, 34, 210, 'column'], [300, 60, 120, 'tank'],
  [420, 40, 250, 'column'], [560, 70, 100, 'tank'], [690, 36, 190, 'column'],
  [830, 44, 270, 'column'], [960, 56, 140, 'tank'], [1090, 34, 230, 'column'],
  [1240, 48, 200, 'column'], [1370, 64, 110, 'tank'], [1490, 36, 175, 'column'],
];
const PLANT_FLARE_X = 1053;

const PLANT_LIGHTS = (() => {
  const rnd = seeded(42);
  const lights = [];
  PLANT_TOWERS.forEach(([x, w, h]) => {
    const n = 4 + Math.floor(rnd() * 5);
    for (let i = 0; i < n; i++) {
      lights.push([x + rnd() * w, 330 - rnd() * h, 1.4 + rnd() * 1.6]);
    }
  });
  for (let i = 0; i < 70; i++) lights.push([rnd() * 1600, 318 - rnd() * 36, 1.2 + rnd() * 1.2]);
  return lights;
})();

const PLANT_STARS = (() => {
  const rnd = seeded(7);
  return Array.from({ length: 46 }, () => [rnd() * 1600, rnd() * 150, 0.5 + rnd() * 1.1]);
})();

const PlantSkyline = () => (
  <svg className="about-banner-art about-banner-art--plant" viewBox="0 0 1600 420"
    preserveAspectRatio="xMidYMax slice" aria-hidden="true" focusable="false">
    <defs>
      <linearGradient id="ab-sky" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0" stopColor="#070a24" />
        <stop offset="0.5" stopColor="#1d1d52" />
        <stop offset="0.85" stopColor="#5a4a8c" />
        <stop offset="1" stopColor="#8a6a9a" />
      </linearGradient>
      <linearGradient id="ab-water" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0" stopColor="#141636" />
        <stop offset="1" stopColor="#070a1f" />
      </linearGradient>
      <radialGradient id="ab-flare" cx="0.5" cy="0.5" r="0.5">
        <stop offset="0" stopColor="#ffb14a" stopOpacity="0.9" />
        <stop offset="1" stopColor="#ff7a1a" stopOpacity="0" />
      </radialGradient>
      <filter id="ab-glow" x="-100%" y="-100%" width="300%" height="300%">
        <feGaussianBlur stdDeviation="2.2" />
      </filter>
    </defs>

    <rect width="1600" height="420" fill="url(#ab-sky)" />
    {/* dusk haze on the horizon so the dark plant shapes stand out against it */}
    <ellipse cx="800" cy="340" rx="900" ry="90" fill="url(#ab-flare)" opacity="0.28" />
    {PLANT_STARS.map(([x, y, r], i) => (
      <circle key={`s${i}`} cx={x} cy={y} r={r} fill="#dfe6ff" opacity="0.55" />
    ))}

    {/* flare stack with flame */}
    <rect x={PLANT_FLARE_X - 3} y="110" width="6" height="225" fill="#0a0e2b" />
    <circle cx={PLANT_FLARE_X} cy="96" r="46" fill="url(#ab-flare)" />
    <path d={`M${PLANT_FLARE_X},72 C${PLANT_FLARE_X + 11},90 ${PLANT_FLARE_X + 7},104 ${PLANT_FLARE_X},110 C${PLANT_FLARE_X - 7},104 ${PLANT_FLARE_X - 11},90 ${PLANT_FLARE_X},72 Z`}
      fill="#ffc15e" />

    {/* plant silhouettes */}
    <g fill="#0a0e2b">
      {PLANT_TOWERS.map(([x, w, h, kind]) => (
        kind === 'column' ? (
          <g key={`t${x}`}>
            <rect x={x} y={335 - h} width={w} height={h} rx={w / 2.6} />
            <rect x={x - 6} y={335 - h * 0.62} width={w + 12} height="4" />
            <rect x={x - 6} y={335 - h * 0.34} width={w + 12} height="4" />
            <rect x={x + w / 2 - 1.5} y={335 - h - 22} width="3" height="22" />
          </g>
        ) : (
          <g key={`t${x}`}>
            <ellipse cx={x + w / 2} cy={335 - h * 0.45} rx={w / 2} ry={h * 0.45} />
            <rect x={x + w * 0.2} y={335 - h * 0.1} width={w * 0.6} height={h * 0.1} />
          </g>
        )
      ))}
      {/* low buildings and pipe racks */}
      <rect x="0" y="300" width="1600" height="40" />
      <rect x="120" y="268" width="150" height="40" />
      <rect x="610" y="262" width="110" height="46" />
      <rect x="1000" y="272" width="170" height="36" />
      <rect x="1300" y="266" width="130" height="42" />
    </g>
    <g stroke="#1a2050" strokeWidth="2">
      <line x1="0" y1="312" x2="1600" y2="312" />
      <line x1="0" y1="322" x2="1600" y2="322" />
    </g>

    {/* lights (blurred copy underneath gives the glow) */}
    <g fill="#ffd58a" filter="url(#ab-glow)" opacity="0.9">
      {PLANT_LIGHTS.map(([x, y, r], i) => <circle key={`g${i}`} cx={x} cy={y} r={r * 2} />)}
    </g>
    <g fill="#fff1c9">
      {PLANT_LIGHTS.map(([x, y, r], i) => <circle key={`l${i}`} cx={x} cy={y} r={r * 0.8} />)}
    </g>

    {/* water and reflections */}
    <rect y="340" width="1600" height="80" fill="url(#ab-water)" />
    <g fill="#ffd58a" opacity="0.28">
      {PLANT_LIGHTS.filter((_, i) => i % 3 === 0).map(([x, , r], i) => (
        <rect key={`w${i}`} x={x - 1} y={346 + (i % 5) * 9} width="2.4" height={8 + r * 5} />
      ))}
    </g>
  </svg>
);

const LabGlassware = () => (
  <svg className="about-banner-art about-banner-art--lab" viewBox="0 0 380 260"
    aria-hidden="true" focusable="false">
    <defs>
      <linearGradient id="ab-liquid" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0" stopColor="#2fe3c8" />
        <stop offset="1" stopColor="#0b8da6" />
      </linearGradient>
    </defs>
    {/* bench */}
    <rect x="0" y="236" width="380" height="24" fill="#070a1f" opacity="0.85" />
    <line x1="0" y1="236" x2="380" y2="236" stroke="#9fb3c4" strokeOpacity="0.35" />

    {/* stand and clamp */}
    <rect x="30" y="40" width="6" height="196" fill="#9fb3c4" opacity="0.55" />
    <rect x="12" y="228" width="42" height="8" rx="2" fill="#9fb3c4" opacity="0.55" />
    <rect x="36" y="74" width="46" height="5" fill="#9fb3c4" opacity="0.55" />

    {/* round-bottom flask */}
    <g stroke="#cfe6f2" strokeOpacity="0.8" strokeWidth="2.2" fill="rgba(207,230,242,0.08)">
      <path d="M96,48 L96,86 C70,98 62,128 62,152 C62,196 90,224 120,224 C150,224 178,196 178,152 C178,128 170,98 144,86 L144,48 Z" />
    </g>
    <path d="M64,150 C84,142 156,160 176,150 C178,196 150,222 120,222 C90,222 62,196 64,150 Z" fill="url(#ab-liquid)" opacity="0.9" />
    <rect x="92" y="40" width="56" height="8" rx="3" fill="#cfe6f2" opacity="0.7" />

    {/* beaker */}
    <g stroke="#cfe6f2" strokeOpacity="0.8" strokeWidth="2.2" fill="rgba(207,230,242,0.08)">
      <path d="M206,120 L206,226 Q206,234 214,234 L268,234 Q276,234 276,226 L276,120 Z" />
    </g>
    <path d="M208,170 L274,170 L274,226 Q274,232 268,232 L214,232 Q208,232 208,226 Z" fill="url(#ab-liquid)" opacity="0.75" />
    {[140, 160, 180, 200].map((y) => (
      <line key={y} x1="206" y1={y} x2="222" y2={y} stroke="#cfe6f2" strokeOpacity="0.7" strokeWidth="1.6" />
    ))}

    {/* test tubes */}
    {[300, 326, 352].map((x, i) => (
      <g key={x}>
        <path d={`M${x},150 L${x},226 Q${x + 9},238 ${x + 18},226 L${x + 18},150`} stroke="#cfe6f2"
          strokeOpacity="0.8" strokeWidth="2.2" fill="rgba(207,230,242,0.08)" />
        <path d={`M${x + 1},${186 - i * 10} L${x + 1},226 Q${x + 9},236 ${x + 17},226 L${x + 17},${186 - i * 10} Z`}
          fill="url(#ab-liquid)" opacity={0.85 - i * 0.12} />
      </g>
    ))}
    <rect x="290" y="196" width="80" height="5" rx="2" fill="#9fb3c4" opacity="0.5" />
  </svg>
);

const AboutBanner = () => (
  <section
    className={BANNER_PHOTO ? 'about-banner about-banner--photo' : 'about-banner'}
    aria-labelledby="about-banner-title"
    style={BANNER_PHOTO ? { backgroundImage: `url(${BANNER_PHOTO})` } : undefined}
  >
    {!BANNER_PHOTO && (
      <>
        <PlantSkyline />
        <LabGlassware />
      </>
    )}
    <div className="about-banner-shade" />
    <div className="about-container about-banner-inner">
      <h1 id="about-banner-title">{BANNER_TITLE}</h1>
      <p>{BANNER_TEXT}</p>
    </div>
  </section>
);

// ── Hero artwork: a reactant / product kinetics curve with measured points ────
const CurveArt = () => (
  <svg
    className="about-curve"
    viewBox="0 0 520 360"
    fill="none"
    role="img"
    aria-label="Reactant concentration falling and product concentration rising over time, with experimental points along each curve"
  >
    {[70, 140, 210, 280].map((y) => (
      <line key={y} x1="30" y1={y} x2="500" y2={y} className="about-curve-grid" />
    ))}
    <line x1="30" y1="330" x2="500" y2="330" className="about-curve-axis" />
    <line x1="30" y1="30" x2="30" y2="330" className="about-curve-axis" />

    <path
      className="about-curve-line about-curve-line--reactant"
      pathLength="1"
      d="M30,60 C120,70 200,150 290,215 C370,270 440,290 500,296"
    />
    <path
      className="about-curve-line about-curve-line--product"
      pathLength="1"
      d="M30,300 C120,290 200,210 290,145 C370,90 440,70 500,64"
    />

    {[[30, 56], [160, 122], [370, 258], [443, 290], [500, 291]].map(([x, y]) => (
      <circle key={`r${x}`} cx={x} cy={y} r="5" className="about-curve-dot about-curve-dot--reactant" />
    ))}
    {[[30, 304], [160, 238], [370, 102], [443, 70], [500, 69]].map(([x, y]) => (
      <circle key={`p${x}`} cx={x} cy={y} r="5" className="about-curve-dot about-curve-dot--product" />
    ))}
  </svg>
);

// ── Page ─────────────────────────────────────────────────────────────────────
const AboutUs = () => {
  const scrollRef = useRef(null);
  const navigate = useNavigate();

  const [registerHelpOpen, setRegisterHelpOpen] = useState(false);

  const go = (path) => (e) => {
    e.preventDefault();
    navigate(path);
  };

  // Navbar clicks. The section tabs live on the landing page, so they go there and
  // the landing page scrolls to the section (it reads `state.scrollTo`).
  const handleNavClick = useCallback((id) => {
    if (id === 'top') { navigate('/'); return; }
    if (id === 'get-started') { navigate('/login'); return; }
    if (id === 'about-us') {
      scrollRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    navigate('/', { state: { scrollTo: id } });
  }, [navigate]);

  return (
    <div className="about-page" ref={scrollRef}>
      <Nav
        scrollContainerRef={scrollRef}
        onNavClick={handleNavClick}
        onOpenRegisterHelp={() => setRegisterHelpOpen(true)}
        currentPageId="about-us"
      />

      <main>
        {/* 0. Banner */}
        <AboutBanner />

        {/* 1. Hero */}
        <section className="about-hero" aria-labelledby="about-hero-title">
          <div className="about-container about-hero-inner">
            <div className="about-hero-copy">
              <h2 id="about-hero-title">{HERO_TITLE}</h2>
              <p>{HERO_TEXT}</p>
              <div className="about-hero-actions">
                <a href="/login" className="about-btn about-btn--primary" onClick={go('/login')}>
                  Start fitting
                </a>
                <a href="/contact" className="about-btn about-btn--ghost" onClick={go('/contact')}>
                  Contact us
                </a>
              </div>
            </div>
            <div className="about-hero-art">
              <CurveArt />
              <p className="about-hero-caption">Reactant (blue) and product (teal) against time, with the measured points the model is fitted to.</p>
            </div>
          </div>
        </section>

        {/* 2. Story */}
        <section className="about-section" aria-labelledby="about-story-title">
          <div className="about-container about-story">
            <div className="about-story-text">
              <h2 id="about-story-title">{STORY_TITLE}</h2>
              {STORY_PARAGRAPHS.map((p) => <p key={p.slice(0, 24)}>{p}</p>)}
            </div>
            <aside className="about-plate" aria-label="What KinetiQ takes off your plate">
              <h3>What you no longer have to do</h3>
              <ul>
                {TAKEN_OFF_YOUR_PLATE.map((item) => <li key={item}>{item}</li>)}
              </ul>
            </aside>
          </div>
        </section>

        {/* 3. Principles */}
        <section className="about-section about-section--tint" aria-labelledby="about-principles-title">
          <div className="about-container">
            <h2 id="about-principles-title" className="about-section-title">What we stand for</h2>
            <dl className="about-principles">
              {PRINCIPLES.map(({ title, body }) => (
                <div className="about-principle" key={title}>
                  <dt>{title}</dt>
                  <dd>{body}</dd>
                </div>
              ))}
            </dl>
          </div>
        </section>

        {/* 4. Team */}
        {TEAM.length > 0 && (
          <section className="about-section" aria-labelledby="about-team-title">
            <div className="about-container">
              <h2 id="about-team-title" className="about-section-title">Our Leadership</h2>
              <ul className="about-team">
                {TEAM.map(({ name, role, bio }, i) => (
                  <li className="about-member" key={`${name}-${i}`}>
                    <span className="about-avatar" aria-hidden="true">{initialsOf(name)}</span>
                    <div>
                      <h3>{name}</h3>
                      <p className="about-member-role">{role}</p>
                      <p>{bio}</p>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </section>
        )}

        {/* 5. Contact band */}
        <section className="about-band" aria-labelledby="about-band-title">
          <div className="about-container about-band-inner">
            <div>
              <h2 id="about-band-title">Have a system you want to fit?</h2>
              <p>Tell us what you are working on, or open KinetiQ and try it on your own data.</p>
            </div>
            <div className="about-band-actions">
              <a href="/login" className="about-btn about-btn--light" onClick={go('/login')}>
                Login / Register
              </a>
              <a href="/contact" className="about-btn about-btn--outline" onClick={go('/contact')}>
                Contact us
              </a>
            </div>
          </div>
        </section>
      </main>

      <footer className="about-footer">
        <div className="about-container">
          <span>&copy; {new Date().getFullYear()} KinetiQ</span>
        </div>
      </footer>

      <RegisterHelpModal
        open={registerHelpOpen}
        onClose={() => setRegisterHelpOpen(false)}
      />
    </div>
  );
};

export default AboutUs;